import { IonBackButton, IonButtons, IonCard, IonCardHeader, IonCardTitle, IonCol, IonContent, IonGrid, IonPage, IonRow, IonTitle, isPlatform } from "@ionic/react";
import { RouteComponentProps } from "react-router";
import MaterialForm from "./components/MaterialForm";
import { useState } from "react";
import { ToolfinderFilterForm, ToolfinderMaterialForm, ToolfinderStandardData, ToolfinderStandardParameterForm, ToolfinderStrategyForm, ToolfinderTrocutData, ToolfinderTrocutParameterForm } from "./components/FormTypes";
import StrategyForm from "./components/StrategyForm";
import StandardParameterForm from "./components/StandardParameterForm";
import { Material } from "../../models/materialModel";
import TrocutParameterForm from "./components/TrocutParameterForm";
import ResultCard from "./components/ResultCard";
import { applyFilter, findStandardTool, findTrocutTool } from "./components/FormHelpers";
import { ArticleSpecs } from "../../models/articleModel";

import "./Toolfinder.scss";
import ToolfinderFilterModal from "./components/FilterModal";
import { Header } from "../../components";
import { useTranslation } from "react-i18next";

interface ToolfinderProps extends RouteComponentProps<{}> { }

const defaultFilterFormValues: ToolfinderFilterForm = {
    l2min: "",
    l2max: "",
    z: "",
    ttype: "",
    coating: "",
    weldon: "",
    ik: "",
    cuttingEdge: ""
}

const Toolfinder: React.FC<ToolfinderProps> = () => {
    const [activeForm, setActiveForm] = useState<number>(1);
    const [material, setMaterial] = useState<Material | undefined>()
    const [strategy, setStrategy] = useState<string | undefined>()
    const [showStrategyData, setShowStrategyData] = useState<boolean>(false)
    const [standardParamForm, setStandardParamForm] = useState<ToolfinderStandardParameterForm | undefined>()
    const [trocutParamForm, setTrocutParamForm] = useState<ToolfinderTrocutParameterForm | undefined>()
    const [result, setResult] = useState<ArticleSpecs[] | undefined>();
    const [filteredRes, setFilteredRes] = useState<ArticleSpecs[] | undefined>();
    const [isFilterModalOpen, setIsFilterModalOpen] = useState<boolean>(false)
    const [filter, setFilter] = useState<ToolfinderFilterForm>(defaultFilterFormValues)
    const [loading, setLoading] = useState<boolean>(true);
    const { t } = useTranslation();

    const materialFormCallback = (data: ToolfinderMaterialForm) => {
        setMaterial(data.material!)
        setActiveForm(activeForm + 1)
    }

    const handleEditFirstFormBtnClick = () => {
        setActiveForm(1)
        setShowStrategyData(false)
        if (strategy === "tro") {
            setTrocutParamForm(undefined)
        } else {
            setStandardParamForm(undefined)
        }
        setResult(undefined)
        setFilter(defaultFilterFormValues)
    }

    const strategyFormCallback = (data: ToolfinderStrategyForm) => {
        setStrategy(data.strategy)
        setActiveForm(activeForm + 1)
        setShowStrategyData(true)
    }

    const handleEditSecondFormBtnClick = () => {
        setActiveForm(2)
        setResult(undefined)
        setFilter(defaultFilterFormValues)
    }

    const standardParameterFormCallback = async (data: ToolfinderStandardParameterForm) => {
        setLoading(true)
        setActiveForm(activeForm + 1)
        setStandardParamForm(data)
        let dataToSend: ToolfinderStandardData = {
            material: material!.gruppe,
            diameter: Number(data.diameter),
            ae: Number(data.ae),
            ap: Number(data.ap),
            strategy: strategy!
        }
        const res = await findStandardTool(dataToSend)
        setResult(res)
        let newFilteredRes = applyFilter(res, filter)
        setFilteredRes(newFilteredRes);
        setLoading(false)
    }

    const trocutParameterFormCallback = async (data: ToolfinderTrocutParameterForm) => {
        setLoading(true)
        setActiveForm(activeForm + 1)
        setTrocutParamForm(data)
        let dataToSend: ToolfinderTrocutData = {
            material: material!.gruppe,
            method: data.method,
            ap: Number(data.ap),
            min_r: data.min_r === "" ? 0 : Number(data.min_r)
        }
        const res = await findTrocutTool(dataToSend)
        setResult(res)
        let newFilteredRes = applyFilter(res, filter)
        setFilteredRes(newFilteredRes);
        setLoading(false)
    }

    const handleEditThirdFormBtnClick = () => {
        setActiveForm(3)
        setResult(undefined)
        setFilter(defaultFilterFormValues)
    }

    const filterSubmitCallback = (data: ToolfinderFilterForm) => {
        setFilter(data);
        let newFilteredRes = applyFilter(result!, data)
        setFilteredRes(newFilteredRes);
        setIsFilterModalOpen(false)
    }




    return (
        <IonPage>
            <Header>
                <IonButtons slot="start">
                    <IonBackButton text={isPlatform('ios') ? t("Zurück") : undefined} />
                </IonButtons>
                <IonTitle>{t("toolfinder.title")}</IonTitle>
            </Header>
            <IonContent>
                <IonGrid fixed={true} className="mainContent">
                    <IonRow>
                        <IonCol size="12">
                            <MaterialForm
                                editBtnCallback={handleEditFirstFormBtnClick}
                                isFormShowing={activeForm === 1}
                                submitForm={materialFormCallback}
                            />
                        </IonCol>

                        {activeForm === 1 &&
                            <IonCol size="12">
                                <IonCard disabled={true}>
                                    <IonCardHeader>
                                        <IonCardTitle>{t("toolfinder.Bearbeitungs-Strategie")}</IonCardTitle>
                                    </IonCardHeader>
                                </IonCard>
                            </IonCol>}

                        <IonCol size="12">
                            <StrategyForm
                                isFormShowing={activeForm === 2}
                                editBtnCallback={handleEditSecondFormBtnClick}
                                submitForm={strategyFormCallback}
                                showStrategyData={showStrategyData}
                            />
                        </IonCol>

                        {activeForm <= 2 &&
                            <IonCol size="12">
                                <IonCard disabled={true}>
                                    <IonCardHeader>
                                        <IonCardTitle>{t("toolfinder.Parameter")}</IonCardTitle>
                                    </IonCardHeader>
                                </IonCard>
                            </IonCol>}

                        {strategy && strategy.startsWith("std") && <IonCol size="12">
                            <StandardParameterForm
                                isFormShowing={activeForm === 3}
                                submitForm={standardParameterFormCallback}
                                strategy={strategy}
                                editBtnCallback={handleEditThirdFormBtnClick}
                                formData={standardParamForm}
                            />
                        </IonCol>}

                        {strategy && strategy === "tro" && <IonCol size="12">
                            <TrocutParameterForm
                                isFormShowing={activeForm === 3}
                                submitForm={trocutParameterFormCallback}
                                strategy={strategy}
                                editBtnCallback={handleEditThirdFormBtnClick}
                                formData={trocutParamForm}
                            />
                        </IonCol>}

                        <IonCol size="12">
                            <ResultCard
                                isCardShowing={activeForm === 4}
                                result={filteredRes}
                                openFilterModal={setIsFilterModalOpen}
                                loading={loading}
                                filter={filter}
                                matGroup={material ? material.gruppe : null}
                                strategy={strategy}
                                ap={strategy === "tro" ? trocutParamForm?.ap : standardParamForm?.ap}
                            />
                        </IonCol>

                    </IonRow>
                </IonGrid>

                <ToolfinderFilterModal
                    isOpen={isFilterModalOpen}
                    setIsOpen={setIsFilterModalOpen}
                    filterValues={filter}
                    submitCallback={filterSubmitCallback}
                    strategy={strategy}
                    ap={strategy === "tro" ? trocutParamForm?.ap : standardParamForm?.ap}
                />
            </IonContent>

        </IonPage>
    )
}

export default Toolfinder;